import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import DOMPurify from 'dompurify';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

// Global state for sound control
let globalLastNotified = { id: null, time: 0 };
let globalIsSpeaking = false;

const DisplayPage = () => {
  const [estado, setEstado] = useState(null);
  const [contenido, setContenido] = useState([]);
  const [currentContentIndex, setCurrentContentIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [turnoRecepcion, setTurnoRecepcion] = useState(null);
  const audioRef = useRef(null);
  const containerRef = useRef(null);
  const wsRef = useRef(null);

  // Play notification - ONLY ONCE
  const playNotification = useCallback((turno) => {
    if (!turno || !turno.numero_formateado) return;
    
    const now = Date.now();
    const destino = turno.destino || turno.punto_nombre || 'Box';
    const turnoId = `${turno.numero_formateado}_${destino}`;
    
    if (globalLastNotified.id === turnoId && (now - globalLastNotified.time) < 10000) return;
    if (globalIsSpeaking) return;
    
    globalLastNotified = { id: turnoId, time: now };
    globalIsSpeaking = true;
    
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {});
    }

    if ('speechSynthesis' in window) {
      speechSynthesis.cancel();
      setTimeout(() => {
        const destinoNombre = turno.destino === 'RECEPCION' ? 'Recepción' : (turno.punto_nombre || `Box ${turno.punto_atencion || 1}`);
        const utterance = new SpeechSynthesisUtterance(`Turno ${turno.numero_formateado}, pase a ${destinoNombre}`);
        utterance.lang = 'es-CL';
        utterance.rate = 0.9;
        utterance.onend = () => { globalIsSpeaking = false; };
        utterance.onerror = () => { globalIsSpeaking = false; };
        speechSynthesis.speak(utterance);
        setTimeout(() => { globalIsSpeaking = false; }, 15000);
      }, 600);
    } else {
      globalIsSpeaking = false;
    }
  }, []);

  const fetchEstado = useCallback(async () => {
    try {
      const [estadoRes, recepcionRes] = await Promise.all([
        axios.get(`${API}/turnos/estado`),
        axios.get(`${API}/turnos/en-recepcion`)
      ]);
      setEstado({ ...estadoRes.data, turnosEnRecepcion: recepcionRes.data });
    } catch (error) {
      console.error('Error:', error);
    }
  }, []);

  const fetchContenido = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/display-content?active_only=true`);
      setContenido(response.data);
    } catch (error) {
      console.error('Error:', error);
    }
  }, []);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  // Carousel timer
  useEffect(() => {
    if (contenido.length === 0) return;
    const duration = (contenido[currentContentIndex]?.duracion || 10) * 1000;
    const timer = setTimeout(() => {
      setCurrentContentIndex((prev) => (prev + 1) % contenido.length);
    }, duration);
    return () => clearTimeout(timer);
  }, [currentContentIndex, contenido]);

  // WebSocket
  useEffect(() => {
    if (wsRef.current) return;
    const wsUrl = process.env.REACT_APP_BACKEND_URL
      ? process.env.REACT_APP_BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://') + '/api/ws/display'
      : `ws://${window.location.host}/api/ws/display`;
    
    let reconnectTimeout = null;
    const connect = () => {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      ws.onopen = () => console.log('[Display] WS connected');
      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          if (message.type === 'TURNO_LLAMADO') {
            playNotification(message.data);
            setTurnoRecepcion(null);
            fetchEstado();
          } else if (message.type === 'TURNO_RECEPCION') {
            const turno = { ...message.data, destino: 'RECEPCION' };
            setTurnoRecepcion(turno);
            playNotification(turno);
            fetchEstado();
            setTimeout(() => setTurnoRecepcion(null), 30000);
          } else if (message.type === 'TURNO_NUEVO' || message.type === 'ESTADO_UPDATE') {
            fetchEstado();
          } else if (message.type === 'TURNOS_REINICIADOS') {
            globalLastNotified = { id: null, time: 0 };
            setTurnoRecepcion(null);
            fetchEstado();
          }
        } catch (e) {
          console.warn('Display WS message parse error:', e);
        }
      };
      ws.onclose = () => { wsRef.current = null; reconnectTimeout = setTimeout(connect, 3000); };
      ws.onerror = (e) => { console.warn('Display WS error:', e); };
    };
    connect();
    return () => {
      if (reconnectTimeout) clearTimeout(reconnectTimeout);
      if (wsRef.current) { wsRef.current.close(); wsRef.current = null; }
    };
  }, [fetchEstado, playNotification]);

  useEffect(() => {
    fetchEstado();
    fetchContenido();
    const i1 = setInterval(fetchEstado, 30000);
    const i2 = setInterval(fetchContenido, 60000);
    return () => { clearInterval(i1); clearInterval(i2); };
  }, [fetchEstado, fetchContenido]);

  const currentContent = contenido[currentContentIndex];
  const turnosPorPunto = estado?.turnos_por_punto || {};
  const puntosActivos = Object.entries(turnosPorPunto).filter(([_, data]) => data.turno);
  const turnosEnRecepcion = estado?.turnosEnRecepcion || [];

  return (
    <div ref={containerRef} className="w-screen h-screen bg-slate-900 text-white overflow-hidden" data-testid="display-page">
      <audio ref={audioRef} preload="auto">
        <source src="https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3" type="audio/mpeg" />
      </audio>

      {/* Fullscreen button */}
      <button onClick={toggleFullscreen} className="absolute top-3 right-3 z-50 p-2 bg-black/30 hover:bg-black/50 rounded-lg">
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
        </svg>
      </button>

      {/* Main Layout: Left (70%) Content | Right (30%) Turnos */}
      <div className="flex h-full">
        
        {/* LEFT SIDE - 70% - Content Area */}
        <div className="w-[70%] h-full flex flex-col">
          
          {/* Header Bar */}
          <div className="h-16 bg-gradient-to-r from-blue-900 to-slate-900 flex items-center justify-between px-6 border-b border-slate-700">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                </svg>
              </div>
              <div>
                <h1 className="text-lg font-bold">Vacu-Track</h1>
                <p className="text-xs text-slate-400">Centro de Vacunación</p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-2xl font-mono font-bold">{new Date().toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' })}</div>
              <div className="text-xs text-slate-400">{new Date().toLocaleDateString('es-CL', { weekday: 'short', day: 'numeric', month: 'short' })}</div>
            </div>
          </div>

          {/* Content Area - Images/Videos */}
          <div className="flex-1 relative bg-black">
            {currentContent ? (
              currentContent.tipo === 'VIDEO' ? (
                <video key={currentContent.id} src={currentContent.contenido} autoPlay muted loop className="w-full h-full object-contain" />
              ) : currentContent.tipo === 'IMAGEN' ? (
                <img key={currentContent.id} src={currentContent.contenido} alt="" className="w-full h-full object-contain" />
              ) : (
                <div className="w-full h-full flex items-center justify-center p-8 text-2xl" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(currentContent.contenido || '', { ALLOWED_TAGS: ['b','i','em','strong','u','br','p','span','div','h1','h2','h3','ul','ol','li','a'], ALLOWED_ATTR: ['href','target','class','style'] }) }} />
              )
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-800 to-slate-900">
                <div className="text-center text-slate-500">
                  <svg className="w-24 h-24 mx-auto mb-4 opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <p className="text-xl">Centro de Vacunación</p>
                </div>
              </div>
            )}
            
            {/* Content indicators */}
            {contenido.length > 1 && (
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                {contenido.map((item, i) => (
                  <div key={item.id || item.url || `slide-${i}`} className={`h-2 rounded-full transition-all ${i === currentContentIndex ? 'w-8 bg-white' : 'w-2 bg-white/40'}`} />
                ))}
              </div>
            )}
          </div>

          {/* Bottom Stats Bar */}
          <div className="h-14 bg-slate-800 flex items-center justify-around border-t border-slate-700">
            <div className="flex items-center gap-2">
              <span className="text-amber-400 text-2xl font-bold">{estado?.esperando || 0}</span>
              <span className="text-slate-400 text-sm">En Espera</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-orange-400 text-2xl font-bold">{turnosEnRecepcion.length}</span>
              <span className="text-slate-400 text-sm">En Recepción</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-blue-400 text-2xl font-bold">{puntosActivos.length}</span>
              <span className="text-slate-400 text-sm">En Atención</span>
            </div>
          </div>
        </div>

        {/* RIGHT SIDE - 30% - Turnos Panel */}
        <div className="w-[30%] h-full bg-slate-800 border-l border-slate-700 flex flex-col">
          
          {/* RECEPCION Section */}
          <div className={`p-4 transition-all duration-500 ${turnoRecepcion ? 'bg-gradient-to-r from-amber-600 to-orange-500' : 'bg-slate-800'}`}>
            <div className="text-center">
              <p className={`text-sm font-semibold mb-2 ${turnoRecepcion ? 'text-amber-100' : 'text-amber-400'}`}>
                📋 PASE A RECEPCIÓN
              </p>
              <div className={`text-6xl font-bold font-mono py-4 rounded-xl ${
                turnoRecepcion 
                  ? 'bg-white/20 text-white animate-pulse-number' 
                  : 'bg-slate-700/50 text-slate-500'
              }`}>
                {turnoRecepcion?.numero_formateado || '---'}
              </div>
            </div>
          </div>

          {/* Turnos en Recepción List */}
          {turnosEnRecepcion.length > 0 && (
            <div className="px-4 py-3 bg-slate-850 border-t border-slate-700">
              <p className="text-xs text-orange-400 mb-2">⏳ Esperando en Recepción:</p>
              <div className="flex flex-wrap gap-2">
                {turnosEnRecepcion.slice(0, 6).map((t) => (
                  <span key={t.id} className="px-3 py-1 bg-orange-500/20 border border-orange-500/40 rounded text-sm font-mono text-orange-300">
                    {t.numero_formateado}
                  </span>
                ))}
                {turnosEnRecepcion.length > 6 && (
                  <span className="px-2 py-1 text-xs text-slate-500">+{turnosEnRecepcion.length - 6}</span>
                )}
              </div>
            </div>
          )}

          {/* Divider */}
          <div className="h-px bg-gradient-to-r from-transparent via-slate-600 to-transparent mx-4" />

          {/* BOX Section */}
          <div className="flex-1 p-4 overflow-y-auto">
            <p className="text-sm font-semibold text-blue-400 mb-3 text-center">
              🏥 PASE AL BOX
            </p>
            
            {puntosActivos.length > 0 ? (
              <div className="space-y-3">
                {puntosActivos.map(([puntoNum, data]) => {
                  const boxNombre = data.punto?.nombre || data.nombre || `Box ${puntoNum}`;
                  const boxColor = data.punto?.color || data.color || '#3B82F6';
                  return (
                  <div
                    key={puntoNum}
                    className="bg-gradient-to-r from-blue-600/20 to-blue-500/10 rounded-xl p-4 border-l-4 animate-pulse-box"
                    style={{ borderLeftColor: boxColor }}
                  >
                    <p
                      className="text-lg lg:text-xl font-bold uppercase tracking-wide text-center mb-2"
                      style={{ color: boxColor }}
                    >
                      {boxNombre}
                    </p>
                    <p className="text-5xl font-bold font-mono text-center" style={{ color: boxColor }}>
                      {data.turno?.numero_formateado || '---'}
                    </p>
                  </div>
                  );
                })}
              </div>
            ) : (
              <div className="flex-1 flex items-center justify-center text-slate-500 py-12">
                <div className="text-center">
                  <svg className="w-12 h-12 mx-auto mb-2 opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-sm">Esperando...</p>
                </div>
              </div>
            )}
          </div>

          {/* Recent Calls */}
          {estado?.ultimos_llamados?.length > 0 && (
            <div className="p-3 bg-slate-900/50 border-t border-slate-700">
              <p className="text-xs text-slate-500 mb-2">Últimos:</p>
              <div className="flex flex-wrap gap-1">
                {estado.ultimos_llamados.slice(0, 5).map((t, i) => (
                  <span key={t.id || i} className="px-2 py-0.5 bg-slate-700 rounded text-xs font-mono text-slate-400">
                    {t.numero_formateado}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes pulse-number {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
        @keyframes pulse-box {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.85; }
        }
        .animate-pulse-number {
          animation: pulse-number 1.5s ease-in-out infinite;
        }
        .animate-pulse-box {
          animation: pulse-box 2s ease-in-out infinite;
        }
        .bg-slate-850 {
          background-color: rgb(25, 33, 47);
        }
      `}</style>
    </div>
  );
};

export default DisplayPage;
